import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // An expired/revoked admin token: drop it once and show the login screen,
  // instead of every admin panel failing silently.
  useEffect(() => {
    const id = axios.interceptors.response.use((r) => r, (err) => {
      const token = localStorage.getItem('samtwo_token');
      const sent = err?.config?.headers?.Authorization || err?.config?.headers?.authorization;
      const code = err?.response?.data?.detail?.error;
      const prompt = typeof code === "string" && (code.startsWith("step_up_") || code.startsWith("confirm_name_"));
      if (err?.response?.status === 401 && !prompt && token && sent === `Bearer ${token}`) {
        localStorage.removeItem('samtwo_token');
        setUser(null);
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('samtwo_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
    } catch {
      localStorage.removeItem('samtwo_token');
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { password, email: email || '' }, { withCredentials: true });
    if (data.requires_2fa) {
      return data;
    }
    if (data.token) localStorage.setItem('samtwo_token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem('samtwo_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
